/**
 * JWT Token Utility Functions
 * Decodes and validates JWT tokens without external dependencies
 */

interface JWTPayload {
    userID: string;
    exp: number;
    iat?: number;
}

/**
 * Decode JWT token (base64 decode the payload)
 * JWT format: header.payload.signature
 */
export const decodeJWT = (token: string): JWTPayload | null => {
    try {
        const parts = token.split('.');
        if (parts.length !== 3) {
            return null;
        }
        
        // Decode the payload (second part)
        const payload = parts[1];
        // Add padding if needed for base64 decoding
        const paddedPayload = payload + '='.repeat((4 - payload.length % 4) % 4);
        const decodedPayload = atob(paddedPayload.replace(/-/g, '+').replace(/_/g, '/'));
        return JSON.parse(decodedPayload) as JWTPayload;
    } catch (error) {
        console.error('Error decoding JWT:', error);
        return null;
    }
};

/**
 * Check if token is expired
 */
export const isTokenExpired = (token: string): boolean => {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) {
        return true; // Consider invalid tokens as expired
    }
    
    // exp is in seconds, Date.now() is in milliseconds
    const expirationTime = decoded.exp * 1000;
    const currentTime = Date.now();
    
    return currentTime >= expirationTime;
};

/**
 * Check if token expires within the next N minutes
 */
export const isTokenExpiringSoon = (token: string, minutes: number = 5): boolean => {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) {
        return true;
    }
    
    const expirationTime = decoded.exp * 1000;
    const currentTime = Date.now();
    const minutesInMs = minutes * 60 * 1000;
    
    return (expirationTime - currentTime) <= minutesInMs;
};

/**
 * Get token expiration time in milliseconds
 */
export const getTokenExpirationTime = (token: string): number | null => {
    const decoded = decodeJWT(token);
    if (!decoded || !decoded.exp) {
        return null;
    }
    
    return decoded.exp * 1000; // Convert to milliseconds
};
